// XP on the result screens, from design/project/App17x-VictoryXP, App18x-RoastXP and App17L-LevelUp: the level bar
// (bright up to the XP before this battle, faded for what it added) and the level-up panel.
import { useEffect, useState } from "react";
import { levelProgress } from "@degenduel/shared";
import { api, ApiError } from "../api.js";

export interface BattleXp {
  result: "win" | "draw" | "loss" | "refund" | "farm";
  xp: number;
  xpBefore: number;
  xpAfter: number;
  levelBefore: number;
  levelAfter: number;
  streakBefore: number;
  streak: number;
  limitBefore: number;
  limitAfter: number;
}

/** What this battle gave the player. The server awards it as the battle settles; asked again briefly until it has. */
export function useBattleXp(battleId: string | null | undefined): BattleXp | null {
  const [xp, setXp] = useState<BattleXp | null>(null);
  useEffect(() => {
    if (!battleId) return;
    let stop = false;
    let tries = 0;
    const ask = () =>
      api<BattleXp>(`/v1/battles/${battleId}/xp`)
        .then((x) => !stop && setXp(x))
        .catch((e) => {
          if (!stop && ++tries < 8 && (!(e instanceof ApiError) || e.status === 404 || e.code === "network")) setTimeout(ask, 1000);
        });
    void ask();
    return () => {
      stop = true;
    };
  }, [battleId]);
  return xp;
}

const UN = { fontFamily: "Unbounded, sans-serif", fontWeight: "900" } as const;
const MONO = { fontFamily: "'JetBrains Mono', monospace", fontWeight: "800" } as const;

/** "LV 4 ▸ 5 · 820 / 1,000 XP" with the bar. After a level up, it shows the new level from its start. */
export function LevelBar({ x, colour = "#CCFF00", style }: { x: BattleXp; colour?: string; style?: React.CSSProperties }) {
  const p = levelProgress(x.xpAfter);
  const span = p.to - p.from;
  const before = x.levelAfter > x.levelBefore ? 0 : Math.max(0, x.xpBefore - p.from);
  const after = x.xpAfter - p.from;
  return (
    <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px", ...style }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ ...UN, fontSize: "10px", letterSpacing: "0.08em" }}>LV {p.level} <span style={{ color: "#8B949E" }}>▸ {p.level + 1}</span></span>
        <span style={{ ...MONO, fontSize: "11px", color: "#8B949E" }}>{x.xpAfter.toLocaleString("en-US")} / {p.to.toLocaleString("en-US")} XP</span>
      </div>
      <div style={{ position: "relative", height: "10px", borderRadius: "5px", background: "#30363D", border: "2px solid #0B0E14", overflow: "hidden" }}>
        <div style={{ position: "absolute", left: "0", top: "0", bottom: "0", width: `${(after / span) * 100}%`, background: colour, opacity: 0.45 }}></div>
        <div style={{ position: "absolute", left: "0", top: "0", bottom: "0", width: `${(before / span) * 100}%`, background: colour }}></div>
      </div>
    </div>
  );
}

const FLAME = (
  <svg width="12" height="12" viewBox="0 0 24 24" style={{ marginLeft: "4px", verticalAlign: "-1px" }} aria-hidden="true">
    <path d="M12 2c1 4 5 6 5 11a5 5 0 0 1-10 0c0-3 2-4 2-7 2 1 3 3 3 5 1-2 1-5 0-9Z" fill="#FF8A3D"></path>
  </svg>
);

export function StreakValue({ streak }: { streak: number }) {
  return <>x{streak}{streak >= 2 && FLAME}</>;
}

/** XP gained, or why none: the 5 a day limit against one opponent. */
export function xpLabel(x: BattleXp | null): string {
  if (!x) return "...";
  if (x.result === "farm") return "MAX";
  return `+${x.xp}`;
}

const SEEN = "dd.levelUpSeen";

/** The level-up panel, once per battle: a new level, or a stake limit that rose (the 20th battle opens 250). */
export function LevelUp({ battleId, x }: { battleId: string; x: BattleXp | null }) {
  const [open, setOpen] = useState(false);
  const show = !!x && (x.levelAfter > x.levelBefore || x.limitAfter > x.limitBefore);
  useEffect(() => {
    if (!show) return;
    try {
      if (localStorage.getItem(SEEN) === battleId) return;
      localStorage.setItem(SEEN, battleId);
    } catch {
      /* private mode: may show again after a restart */
    }
    setOpen(true);
  }, [show, battleId]);
  if (!open || !x) return null;
  const levelled = x.levelAfter > x.levelBefore;
  const next = levelProgress(x.xpAfter).to;
  return (
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: "0", zIndex: 50, background: "rgba(11,14,20,0.8)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
      <div style={{ boxSizing: "border-box", width: "100%", maxWidth: "360px", maxHeight: "100%", overflowY: "auto", padding: "26px 20px 20px", borderRadius: "26px", background: "#161B22", border: "3px solid #CCFF00", boxShadow: "0 0 60px rgba(204,255,0,0.3), 8px 8px 0 #0B0E14", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <span style={{ height: "26px", padding: "0 10px", display: "flex", alignItems: "center", background: "#CCFF00", color: "#0B0E14", border: "2px solid #0B0E14", transform: "skewX(-10deg)", ...UN, fontSize: "10px", letterSpacing: "0.08em" }}>{levelled ? "LEVEL UP" : "NEW STAKE LIMIT"}</span>
        <div style={{ marginTop: "16px", ...UN, fontSize: "64px", lineHeight: "1", color: "#CCFF00", transform: "skewX(-8deg)", textShadow: "4px 4px 0 #0B0E14, 8px 8px 0 #FF3366", whiteSpace: "nowrap" }}>{levelled ? `LV ${x.levelAfter}` : `${x.limitAfter.toLocaleString("en-US")}`}</div>
        {x.limitAfter > x.limitBefore && (
          <div style={{ marginTop: "18px", width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: "14px", background: "rgba(204,255,0,0.08)", border: "2px solid rgba(204,255,0,0.3)", display: "flex", alignItems: "center", gap: "12px" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2.4" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"></rect><path d="M8 11V7a4 4 0 0 1 7.5-2"></path></svg>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ ...UN, fontSize: "13px" }}>{x.limitAfter.toLocaleString("en-US")} USDT STAKES</span>
              <span style={{ fontSize: "12px", color: "#8B949E" }}>Your limit was {x.limitBefore.toLocaleString("en-US")}</span>
            </div>
          </div>
        )}
        <span style={{ marginTop: "12px", fontSize: "12px", color: "#8B949E" }}>Next: LV {levelProgress(x.xpAfter).level + 1} at {next.toLocaleString("en-US")} XP</span>
        <button onClick={() => setOpen(false)} style={{ marginTop: "18px", width: "100%", height: "54px", border: "2px solid #0B0E14", borderRadius: "16px", background: "#CCFF00", color: "#0B0E14", ...UN, fontSize: "15px", boxShadow: "0 5px 0 #5E7500, 0 5px 0 2px #0B0E14", cursor: "pointer" }}>LET'S GO</button>
      </div>
    </div>
  );
}
